import { useEffect, useState } from 'react';
import { useTranslation } from '../../../lib/i18n';
import { messageDir } from '../../../lib/message-direction';
import type { TaskDetail, TaskNote } from '../../../lib/use-task-detail';
import type { TaskEditsFlow } from '../../../lib/use-task-edits';
import { Button, Card, Input, Typography } from '../ui';
import styles from './TaskUserEdits.module.css';

/**
 * The two things a person can do TO a task, as opposed to with it: restate it, and note on it.
 *
 * Both were asked for in one sentence — *"I should see the conversation, I should be able to modify the
 * task, to cancel it, to add notes on the task"* — and the screen shipped with only two of the four.
 * They live in one module, and one module rendered by BOTH layouts, because they are the same decision
 * twice: the desktop modal and the phone sheet are different chrome over the same panel, and a second
 * copy of a form is a second set of validation rules to drift.
 *
 * Three rules worth not breaking:
 *
 * - **A task that is over accepts neither.** The gate is `lib/task-editable.ts`, which is an alias of
 *   the CANCEL rule rather than a second status table — a screen that offers a note where it hides a
 *   cancel is the drift that costs. The server refuses independently with a 409, so a task that settles
 *   while the form is open is caught rather than written.
 * - **An edit writes the task's BRIEF, not its summary.** The summary is prose the model wrote about how
 *   the task is going, in the user's language; the brief is the spec the agent works from. Restating a
 *   task means changing the second.
 * - **A note goes on the change log under the `note` label** — the channel the agent already reads —
 *   which is also why an existing note stays visible after the task settles. It is history by then, and
 *   history does not stop being true.
 */
export function TaskUserEdits({
  task,
  editable,
  edits,
  notes,
}: {
  task: Pick<TaskDetail, 'id' | 'title' | 'brief'>;
  editable: boolean;
  edits: TaskEditsFlow;
  notes: TaskNote[];
}) {
  return (
    <>
      <TaskBriefSection task={task} editable={editable} edits={edits} />
      <TaskNotesSection notes={notes} editable={editable} edits={edits} />
    </>
  );
}

/**
 * The task's BRIEF — what it was asked to do — shown as content, with the edit affordance beside it.
 *
 * **The brief used to be visible only from INSIDE the edit form**, behind an "Edit task" button that
 * floated under the status badge naming nothing. On a task carrying no agent-authored `summary` that
 * made the whole drill-in read as empty — a title, a badge, and a button — while the one field that
 * says what the task IS sat one click away, in a form. Reported on exactly that shape: a migrated v1
 * task, whose `display.summary` the v1→v2 mapper never populates (`TaskV1Mapper.buildDisplay` copies
 * `summary`/`currentAction` only when the v1 task carried them) while `notes[0]` survives AS the brief.
 * So the content was there the whole time and nothing rendered it.
 *
 * It is deliberately NOT folded into the description above: `summary` is the agent's account of where
 * the work got to and `brief` is the spec it works from, and the header of this file already turns on
 * that distinction ("An edit writes the task's BRIEF, not its summary"). Two fields, two blocks — a
 * `summary || brief` fallback would put the agent's voice and the user's ask in one paragraph and make
 * the edit button's target ambiguous again.
 *
 * Shown whatever the task's state, like the notes below it and for the same reason: the ask is history
 * on a settled task, and history does not stop being true. Only the EDIT affordance is gated on
 * `editable`. With no brief and nothing to write it with, the section renders nothing at all rather than
 * a heading over a sentence explaining an absence nobody can act on.
 */
function TaskBriefSection({
  task,
  editable,
  edits,
}: {
  task: Pick<TaskDetail, 'id' | 'title' | 'brief'>;
  editable: boolean;
  edits: TaskEditsFlow;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const brief = task.brief?.trim() ?? '';

  if (open) return <TaskEditForm task={task} edits={edits} onClose={() => setOpen(false)} />;
  if (!brief && !editable) return null;

  return (
    <section className={styles.brief}>
      <div className={styles.briefHead}>
        <Typography variant="h4" component="h3" className={styles.briefTitle}>
          {t('tasks.edit.briefLabel')}
        </Typography>
        {editable && (
          <Button variant="text" size="small" onClick={() => { edits.clearFailure(); setOpen(true); }}>
            {t('tasks.edit.action')}
          </Button>
        )}
      </div>
      {brief ? (
        // The user's own words — direction is decided by the text, not the app locale, same as a note.
        <p className={styles.briefBody} dir={messageDir(brief)}>
          {brief}
        </p>
      ) : (
        <div className={styles.briefEmpty}>{t('tasks.brief.empty')}</div>
      )}
    </section>
  );
}

/**
 * Restate the task, inline — the same idiom the agent hero's rename uses (a field in place, committed
 * on Save), never a second modal on top of the drill-in.
 *
 * The draft is seeded from the REAL current values and reseeded whenever they change under it, so a
 * refetch landing mid-edit does not leave the form describing a task that has moved. A blank name blocks
 * Save with an inline error and sends nothing — the same rule the Maestro editor's name field follows,
 * and the same rule the server enforces.
 */
function TaskEditForm({
  task,
  edits,
  onClose,
}: {
  task: Pick<TaskDetail, 'id' | 'title' | 'brief'>;
  edits: TaskEditsFlow;
  /** Closing is the OWNER's business now — the brief block above swaps itself for this form, so the
   *  form cannot also decide whether it is mounted without the two disagreeing. */
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [title, setTitle] = useState(task.title);
  const [brief, setBrief] = useState(task.brief ?? '');
  const [blank, setBlank] = useState(false);

  // Reseed from the entity while the form is MOUNTED, so a refetch landing under an open form does not
  // leave the draft describing a task that has moved. It used to key on the old local `open` flag; the
  // form now only exists while open, so the entity's own identity is the whole dependency.
  useEffect(() => {
    setTitle(task.title);
    setBrief(task.brief ?? '');
    setBlank(false);
  }, [task.id]);

  const onSave = async () => {
    if (!title.trim()) {
      setBlank(true);
      return;
    }
    setBlank(false);
    const changed = {
      ...(title.trim() !== task.title.trim() ? { title: title.trim() } : {}),
      ...(brief.trim() !== (task.brief ?? '').trim() ? { brief: brief.trim() } : {}),
    };
    // An untouched Save writes nothing — the same rule `lib/routine-edit.ts` follows. Sending an
    // unchanged field is a real write: it bumps the revision and can lose a concurrent one.
    if (Object.keys(changed).length === 0) {
      onClose();
      return;
    }
    if (await edits.save(changed)) onClose();
  };

  return (
    // The DS sunken Card, not a hand-rolled surface: the form is a panel inside the drill-in, and this
    // repo has a guard (`test/card-spec.test.ts`) that exists because every hand-rolled one drifted.
    <Card variant="sunken" size="sm" component="section" className={styles.editForm}>
      <Input
        label={t('tasks.edit.titleLabel')}
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        maxLength={200}
        disabled={edits.busy}
        status={blank ? 'error' : 'default'}
        statusMessage={blank ? t('tasks.edit.titleRequired') : undefined}
      />
      <label className={styles.briefLabel}>
        <span className={styles.briefLabelText}>{t('tasks.edit.briefLabel')}</span>
        <textarea
          className={styles.briefField}
          value={brief}
          onChange={(e) => setBrief(e.target.value)}
          maxLength={4000}
          rows={4}
          disabled={edits.busy}
          dir={messageDir(brief)}
        />
      </label>
      <TaskWriteFailureLine failure={edits.failure} settledKey="tasks.edit.settled" errorKey="tasks.edit.error" />
      <div className={styles.editActions}>
        <Button variant="outlined" size="small" onClick={onClose} disabled={edits.busy}>
          {t('common.cancel')}
        </Button>
        <Button size="small" onClick={onSave} loading={edits.busy}>
          {t('common.save')}
        </Button>
      </div>
    </Card>
  );
}

/**
 * The notes the user has written on this task, and the field to add one.
 *
 * Existing notes are shown whatever the task's state — they are history, and a settled task's notes are
 * still the record of what was said. Only the COMPOSER is gated: a note is written to be read by the
 * agent working the task, and on a finished task there is nobody to read it.
 *
 * The empty state is a sentence, never a bare heading over nothing.
 */
function TaskNotesSection({ notes, editable, edits }: { notes: TaskNote[]; editable: boolean; edits: TaskEditsFlow }) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState('');

  const onAdd = async () => {
    if (!draft.trim()) return;
    if (await edits.addNote(draft.trim())) setDraft('');
  };

  return (
    <section className={styles.notes}>
      <Typography variant="h4" component="h3" className={styles.notesTitle}>
        {t('tasks.notes.title')}
      </Typography>
      {notes.length === 0 ? (
        <div className={styles.notesEmpty}>{t('tasks.notes.empty')}</div>
      ) : (
        <ul className={styles.noteList}>
          {notes.map((note) => (
            <li key={note.id} className={styles.note}>
              {/* The user's own words — direction is decided by the text, not by the app locale. */}
              <span className={styles.noteBody} dir={messageDir(note.content)}>
                {note.content}
              </span>
              {formatNoteWhen(note.date) && <span className={styles.noteWhen}>{formatNoteWhen(note.date)}</span>}
            </li>
          ))}
        </ul>
      )}
      {editable && (
        <div className={styles.noteComposer}>
          <textarea
            className={styles.noteField}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder={t('tasks.notes.placeholder')}
            maxLength={2000}
            rows={2}
            disabled={edits.busy}
            dir={messageDir(draft)}
          />
          <TaskWriteFailureLine failure={edits.failure} settledKey="tasks.edit.settled" errorKey="tasks.notes.error" />
          <div className={styles.noteActions}>
            <Button size="small" onClick={onAdd} loading={edits.busy} disabled={!draft.trim()}>
              {t('tasks.notes.add')}
            </Button>
          </div>
        </div>
      )}
    </section>
  );
}

/** A refusal, in the right words. A 409 is not "try again" — the task finished, and a retry cannot work. */
function TaskWriteFailureLine({
  failure,
  settledKey,
  errorKey,
}: {
  failure: TaskEditsFlow['failure'];
  settledKey: string;
  errorKey: string;
}) {
  const { t } = useTranslation();
  if (!failure) return null;
  return (
    <div className={styles.failure} role="alert">
      {t(failure === 'settled' ? settledKey : errorKey)}
    </div>
  );
}

/** A note's instant, in the reader's own locale. Empty when missing or unparseable — a note with no
 *  readable date says nothing about one rather than rendering "Invalid Date". */
function formatNoteWhen(value: string): string {
  if (!value) return '';
  const at = new Date(value);
  return Number.isNaN(at.getTime()) ? '' : at.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}
